import { CSSObject } from '@emotion/core';
import { useCallback, useEffect, useState } from 'react';

import { RangeSlider } from '@controls/RangeSlider';

import { Layout, colors, scale, typography } from '@scripts/gds';
import { usePrevious } from '@scripts/hooks';

export interface RangeFilterProp {
    code: string;
    value: number[];
    max: number;
    min: number;
    step?: number;
    isDisabled?: boolean;
    onChange: (value: number[]) => void;
    onAfterChange?: (value: number[]) => void;
}

const labelStyles: CSSObject = {
    color: colors.textSecondGrey,
    ...typography('bodyXXSRegular'),
    marginRight: scale(1),
};

export const RangeFilter = ({
    code,
    value,
    max,
    min,
    step = 1,
    isDisabled = false,
    onChange,
    onAfterChange,
}: RangeFilterProp) => {
    const [rangeValue, setRangeValue] = useState<number[]>(value);
    const [inputValue, setInputValue] = useState<(number | string)[]>(value);

    const prevValue = usePrevious(value);

    const getValue = useCallback(
        () =>
            inputValue.map((iValue, i) => {
                const numValue = Number(iValue);

                if (i === 0) {
                    if (numValue < min) return min;

                    /** Проверяем чтобы все значения находились в дипазоне */
                    if (
                        numValue > Number(inputValue[i + 1]) &&
                        Number(inputValue[i + 1]) > min &&
                        Number(inputValue[i + 1]) <= max
                    ) {
                        return Number(inputValue[i + 1]) - 1;
                    }

                    return numValue;
                }

                if (numValue > max) return max;

                if (
                    numValue < Number(inputValue[i - 1]) &&
                    Number(inputValue[i - 1]) >= min &&
                    Number(inputValue[i - 1]) < max
                ) {
                    return Number(inputValue[i - 1]) + 1;
                }

                return numValue;
            }),
        [inputValue, max, min]
    );

    useEffect(() => {
        if (value !== prevValue) {
            setRangeValue(getValue());
            setInputValue(getValue());
        }
    }, [value, prevValue]);

    return (
        <Layout
            cols={2}
            gap={`${scale(3, true)}px 0`}
            css={{
                marginBottom: scale(2),
                position: 'relative',
            }}
        >
            {inputValue.map((iValue, i) => (
                <Layout.Item css={{ position: 'relative', display: 'flex', alignItems: 'center' }} key={i}>
                    {i === 0 ? (
                        <span css={labelStyles}>от</span>
                    ) : (
                        <span css={{ ...labelStyles, marginLeft: scale(1) }}>до</span>
                    )}
                    <input
                        type="number"
                        id={`range-filter-field-${code}-${i}`}
                        value={iValue}
                        onChange={e => {
                            const arrValue = Array.from(inputValue);
                            arrValue[i] = e.target.value;
                            setInputValue(arrValue);
                        }}
                        onBlur={() => {
                            setInputValue(getValue().map(v => String(v).replace(/^0+/, '0')));
                            setRangeValue(getValue());
                            onChange(getValue());
                        }}
                        css={{
                            width: '100%',
                            height: scale(6),
                            padding: `${scale(3, true)}px ${scale(2)}px `,
                            border: `1px solid ${colors.borderMain}`,
                            borderRadius: 10,
                            color: colors?.black,
                            WebkitAppearance: 'none',
                            ':focus': {
                                outline: 'none',
                                borderColor: colors?.primary,
                            },
                            ':disabled': {
                                background: colors?.grey200,
                                color: colors?.grey400,
                                cursor: 'not-allowed',
                            },
                            '::-webkit-outer-spin-button, ::-webkit-inner-spin-button': {
                                display: 'none',
                            },
                        }}
                        disabled={isDisabled}
                    />
                </Layout.Item>
            ))}
            <Layout.Item col={2} css={{ padding: `0 ${scale(1)}px` }}>
                <RangeSlider
                    min={min}
                    max={max}
                    step={step}
                    value={rangeValue}
                    disabled={isDisabled}
                    onChange={rsValue => {
                        setRangeValue(rsValue);
                        setInputValue(rsValue);
                        onChange(rsValue);
                    }}
                    allowCross={false}
                    onAfterChange={onAfterChange}
                />
            </Layout.Item>
        </Layout>
    );
};
